// src/components/outputs/blogSeo/ArticleView.jsx — the «Testo» tab of the Blog SEO output.
//
// The article as the design draws it (Figma «Content Generator 1.1», node
// 828:13480): the H1 large and dark, every H2 in the accent, every H3 with the
// blocks that follow it in an indented block with a left rule, true lists, then
// the FAQ («FAQ incluse nel contenuto»: question in bold, answer) and the extra
// lists the run carries (cited sources, internal links) — each only when the
// piece exists. The conclusion is part of the article piece (its own H2).
//
// Everything is drawn from the PIECES of publish/pieces/blogSeo.js (the
// structured fields, normalised once) — ⛔ never from body_html, never through
// innerHTML. Headings are REAL h1 / h2 / h3 elements, so a hand selection
// (cleanCopy.js on the panel that hosts this view) keeps them; the accent, the
// rule and the indent are inline styles, which cleanCopy drops — the clipboard
// gets h1/h2/h3, p, ul/ol/li, strong/em, a and nothing of the look.
// Paragraphs and lists are BlocksView's (same type scale, same list markers);
// only the headings are drawn here, because BlocksView scales a heading one
// level down for the compact card.
//
//   groupArticle(blocks) → [{ type: 'h1' | 'h2', heading }, { type: 'sub',
//     heading, blocks }, { type: 'blocks', blocks }] — a flat block list grouped
//     so that every H3 owns the blocks up to the next heading; consecutive
//     plain blocks share one group (BlocksView spaces them).
//
//   ArticleView({ article, faq, extras, t, w, color })
//     article   the 'article' piece (required)
//     faq       the 'faq' piece or null
//     extras    [{ id, label, blocks }] — the 'sources' / 'internal_links'
//               pieces, drawn as a labelled list each; [] draws nothing
//     t, w      the surface theme and its rgba helper; color the accent

import React from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, List as ListIcon } from 'lucide-react';
import { COLORS, FONT_DISPLAY, FONT_TEXT } from '../../../constants/theme.js';
import { asBlocks, asRuns } from '../../../publish/blocks.js';
import BlocksView, { renderRuns } from '../pieces/BlocksView.jsx';

const defaultW = (o) => `rgba(255,255,255,${o})`;

export function groupArticle(blocks) {
  const out = [];
  let sub = null;
  let plain = null;
  for (const b of (Array.isArray(blocks) ? asBlocks(blocks) : [])) {
    if (b.type === 'heading') {
      plain = null;
      const level = Number(b.level) || 2;
      if (level >= 3) {
        sub = { type: 'sub', heading: b, blocks: [] };
        out.push(sub);
      } else {
        sub = null;
        out.push({ type: level === 1 ? 'h1' : 'h2', heading: b });
      }
      continue;
    }
    if (sub) { sub.blocks.push(b); continue; }
    if (!plain) { plain = { type: 'blocks', blocks: [] }; out.push(plain); }
    plain.blocks.push(b);
  }
  return out;
}

const qaBlocks = (piece) => asBlocks(piece?.blocks).filter((b) => b.type === 'qa');

function SectionHeading({ icon: Icon, children, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
      <Icon size={20} color={accent} strokeWidth={1.75} aria-hidden="true" />
      <span style={{ fontFamily: FONT_TEXT, fontSize: '12px', fontWeight: 600, lineHeight: '16px', textTransform: 'uppercase', color: accent }}>
        {children}
      </span>
    </div>
  );
}

export default function ArticleView({ article, faq = null, extras = [], t = {}, w = defaultW, color }) {
  const { t: tr } = useTranslation('outputs');
  const theme = t && typeof t === 'object' ? t : {};
  const text = theme.text || COLORS.ink;
  const border = theme.border || COLORS.border;
  const accent = color || COLORS.accent;
  const ctx = { accent, muted: theme.textMuted || COLORS.textSecondary };
  const groups = groupArticle(article?.blocks);
  const qas = qaBlocks(faq);
  const lists = (Array.isArray(extras) ? extras : []).filter((e) => e && asBlocks(e.blocks).length);

  return (
    <div data-testid="blogseo-article" style={{ display: 'flex', flexDirection: 'column', gap: '18px', fontFamily: FONT_DISPLAY, color: text, minWidth: 0 }}>
      {groups.map((g, i) => {
        if (g.type === 'h1') {
          return (
            <h1 key={i} style={{ margin: 0, fontFamily: FONT_DISPLAY, fontSize: '30px', fontWeight: 700, letterSpacing: '-0.6px', lineHeight: 1.25, color: text }}>
              {renderRuns(g.heading.runs, ctx)}
            </h1>
          );
        }
        if (g.type === 'h2') {
          return (
            <h2 key={i} style={{ margin: 0, fontFamily: FONT_DISPLAY, fontSize: '18px', fontWeight: 600, lineHeight: 1.6, color: accent }}>
              {renderRuns(g.heading.runs, ctx)}
            </h2>
          );
        }
        if (g.type === 'sub') {
          return (
            <div key={i} style={{ borderLeft: `2px solid ${border}`, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
              <h3 style={{ margin: 0, fontFamily: FONT_DISPLAY, fontSize: '14px', fontWeight: 700, lineHeight: 1.6, color: text }}>
                {renderRuns(g.heading.runs, ctx)}
              </h3>
              <BlocksView blocks={g.blocks} t={theme} w={w} color={accent} />
            </div>
          );
        }
        return <BlocksView key={i} blocks={g.blocks} t={theme} w={w} color={accent} />;
      })}

      {qas.length > 0 && (
        <div data-testid="blogseo-faq" style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingTop: '24px', borderTop: `1px solid ${border}` }}>
          <SectionHeading icon={HelpCircle} accent={accent}>{tr('blogSeo.faqHeading')}</SectionHeading>
          {qas.map((qa, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {asRuns(qa.question).length > 0 && (
                <p style={{ margin: 0, fontFamily: FONT_TEXT, fontSize: '16px', fontWeight: 700, lineHeight: '24px', whiteSpace: 'pre-wrap', color: text }}>
                  <strong>{renderRuns(qa.question, ctx)}</strong>
                </p>
              )}
              <BlocksView blocks={qa.answer} t={theme} w={w} color={accent} />
            </div>
          ))}
        </div>
      )}

      {lists.map((extra) => (
        <div key={extra.id} data-testid={`blogseo-extra-${extra.id}`} style={{ display: 'flex', flexDirection: 'column', gap: '14px', paddingTop: '24px', borderTop: `1px solid ${border}` }}>
          <SectionHeading icon={ListIcon} accent={accent}>{extra.label}</SectionHeading>
          <BlocksView blocks={extra.blocks} t={theme} w={w} color={accent} />
        </div>
      ))}
    </div>
  );
}
